import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  LogOut, Calendar, IndianRupee, Plus, Film, Video, Image,
  Layers, Sparkles, Play, Pause, CheckCircle2, User, Briefcase, Phone,
  Mail, MapPin, Eye, Zap, Heart, Clock, Coffee, Sun,
  Sunrise, Sunset, Moon, Camera, Users, Package, FileCheck,
  Star, ArrowRight, Wallet, ListTodo
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

const TYPE_ICONS = {
  'Short-form Video': <Film size={14} className="text-pink-400" />,
  'Long-form Video': <Video size={14} className="text-purple-400" />,
  'Static': <Image size={14} className="text-blue-400" />,
  'Carousel': <Layers size={14} className="text-amber-400" />,
  'Motion Graphic': <Sparkles size={14} className="text-cyan-400" />,
};

const STATUS_COLORS = {
  'Pending': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'In Progress': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  'Paused': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Completed': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Approved': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Rejected': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

const TRACKER_ICONS = {
  production: <Film size={20} className="text-pink-400" />,
  shoots: <Camera size={20} className="text-purple-400" />,
  clients: <Users size={20} className="text-cyan-400" />,
  deliverables: <FileCheck size={20} className="text-emerald-400" />,
  attendance: <Calendar size={20} className="text-blue-400" />,
  assets: <Package size={20} className="text-amber-400" />,
  talent: <Star size={20} className="text-yellow-400" />,
};

const TRACKER_ROUTES = {
  production: '/production', shoots: '/shoots', clients: '/clients',
  deliverables: '/deliverables', attendance: '/attendance', assets: '/assets', talent: '/talent',
};

const TRACKER_LABELS = {
  production: 'Production', shoots: 'Shoots', clients: 'Clients',
  deliverables: 'Deliverables', attendance: 'Attendance', assets: 'Assets', talent: 'Talent',
};

const PERM_STYLES = {
  view: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', label: 'View Only', icon: Eye },
  access: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'Can Work', icon: Zap },
};

// ===== TIME UTILS =====
function getGreeting(hour, name) {
  const first = name?.split(' ')[0] || 'there';
  if (hour < 12) return `Good morning, ${first}`;
  if (hour < 17) return `Good afternoon, ${first}`;
  return `Good evening, ${first}`;
}

const DAILY_READS = [
  { title: "The Power of Showing Up", body: "Success isn't always about greatness. It's about consistency. Consistent hard work leads to success. Greatness will come. Every day you show up, you're building something." },
  { title: "Why Small Wins Matter", body: "We often chase the big milestones and forget that every great achievement is built on a thousand small wins. Celebrate the small stuff \u2014 it's the fuel that keeps you going." },
  { title: "The Art of Deep Work", body: "In a world full of distractions, the ability to focus deeply is a superpower. Block out 90 minutes today \u2014 no phone, no notifications \u2014 and watch what you can create." },
  { title: "Growth Happens in Discomfort", body: "If everything feels easy, you're not growing. The tasks that challenge you, the projects that stretch your skills \u2014 that's where the magic happens." },
  { title: "Be a Learn-It-All", body: "The most successful people aren't the ones who have all the answers \u2014 they're the ones who never stop asking questions." },
  { title: "Your Energy is Your Currency", body: "Protect your energy like you protect your time. Say no to things that drain you. You can't pour from an empty cup." },
  { title: "The 1% Rule", body: "Just aim to be 1% better than yesterday. Over a year, that's 37x growth. Small, daily improvements are the foundation of extraordinary results." },
  { title: "Collaboration Over Competition", body: "The best teams aren't made of individuals trying to outshine each other. They're made of people who lift each other up." },
  { title: "Own Your Mistakes", body: "Mistakes aren't failures \u2014 they're data. The people who grow fastest own their mistakes, learn from them, and move forward." },
  { title: "Rest is Productive", body: "Science says rest improves creativity, decision-making, and focus. Taking breaks isn't lazy \u2014 it's strategic." },
];

function getDailyRead() {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  return DAILY_READS[dayOfYear % DAILY_READS.length];
}

// ===== MAIN DASHBOARD =====
export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const perms = user?.effective_permissions || {};

  const [tasks, setTasks] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [leaveApps, setLeaveApps] = useState([]);
  const [profile, setProfile] = useState(null);
  const [punchData, setPunchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReimburse, setShowReimburse] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showComplete, setShowComplete] = useState(null);
  const [finalLink, setFinalLink] = useState('');
  const [now, setNow] = useState(new Date());

  const dailyRead = getDailyRead();

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, reimbRes, leaveRes, profileRes, punchRes] = await Promise.all([
        axios.get(`${API}/production/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/reimbursements`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/leave-applications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/employee/profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/punch/status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setTasks(tasksRes.data);
      setReimbursements(reimbRes.data);
      setLeaveApps(leaveRes.data);
      setProfile(profileRes.data);
      setPunchData(punchRes.data?.status === 'not_punched' ? null : punchRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePunchIn = async () => {
    try {
      await axios.post(`${API}/punch/in`, {}, { withCredentials: true });
      toast.success("Punched in!");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePunchOut = async () => {
    try {
      const res = await axios.post(`${API}/punch/out`, {}, { withCredentials: true });
      toast.success(`Punched out! ${res.data.total_hours?.toFixed(1)} hours logged.`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleTaskAction = async (taskId, action, extraData) => {
    try {
      if (action === 'complete') {
        await axios.post(`${API}/production/tasks/${taskId}/complete`, extraData, { withCredentials: true });
        toast.success('Task completed');
      } else {
        await axios.post(`${API}/production/tasks/${taskId}/${action}`, {}, { withCredentials: true });
        toast.success(action === 'start' ? 'Task started' : 'Task paused');
      }
      setShowComplete(null); setFinalLink(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const VALID_TRACKERS = new Set(['production', 'shoots', 'clients', 'deliverables', 'attendance', 'assets', 'talent']);
  const trackerList = Object.entries(perms).filter(([k, v]) => v && VALID_TRACKERS.has(k)).map(([k, v]) => ({
    key: k, label: TRACKER_LABELS[k] || k, access: v,
  }));

  const hour = now.getHours();
  const greeting = getGreeting(hour, user?.name);
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' });

  const isPunchedIn = punchData?.punch_in && !punchData?.punch_out;
  const isPunchedOut = !!punchData?.punch_out;

  // Elapsed timer
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!isPunchedIn) return;
    const id = setInterval(() => {
      const diff = Date.now() - new Date(punchData.punch_in).getTime();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setElapsed(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(id);
  }, [isPunchedIn, punchData]);

  if (loading) {
    return <SidebarLayout title="Home"><div className="flex items-center justify-center py-20 text-slate-500">Loading...</div></SidebarLayout>;
  }

  // Determine which tab to show
  const tab = tabParam || 'home';

  return (
    <SidebarLayout title={tab === 'home' ? '' : tab.charAt(0).toUpperCase() + tab.slice(1)}>
      {/* HOME */}
      {tab === 'home' && (
        <div className="space-y-6 animate-fadeIn" data-testid="emp-home">
          {/* Big Clock + Greeting */}
          <div className="text-center py-6" data-testid="live-clock">
            <p className="text-slate-500 text-sm">{greeting}</p>
            <div className="text-5xl sm:text-6xl font-heading font-black text-white tracking-tight mt-2 font-mono" data-testid="big-clock">
              {timeStr}
            </div>
            <p className="text-slate-600 text-xs mt-2">{dateStr}</p>
          </div>

          {/* Punch In/Out */}
          <div className="glass-card p-5" data-testid="punch-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${isPunchedIn ? 'bg-emerald-400 animate-pulse' : isPunchedOut ? 'bg-slate-600' : 'bg-amber-400'}`} />
                {isPunchedIn && (
                  <div>
                    <span className="text-emerald-400 text-sm font-medium">Working</span>
                    <span className="text-white font-mono text-lg font-bold ml-3" data-testid="punch-elapsed">{elapsed}</span>
                  </div>
                )}
                {isPunchedOut && <span className="text-slate-400 text-sm">{punchData.total_hours?.toFixed(1)} hours logged today</span>}
                {!punchData?.punch_in && <span className="text-amber-400/70 text-sm">You haven't punched in yet</span>}
              </div>
              <div>
                {!punchData?.punch_in && (
                  <Button onClick={handlePunchIn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6" data-testid="punch-in-btn">Punch In</Button>
                )}
                {isPunchedIn && (
                  <Button onClick={handlePunchOut} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-6" data-testid="punch-out-btn">Punch Out</Button>
                )}
                {isPunchedOut && (
                  <span className="text-[11px] px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">Done for today</span>
                )}
              </div>
            </div>
          </div>

          {/* Daily Read */}
          <div className="glass-card p-5 border-l-2 border-indigo-500/40" data-testid="daily-read">
            <div className="flex items-center gap-2 mb-2">
              <Heart size={13} className="text-indigo-400" />
              <span className="text-[10px] font-semibold tracking-widest uppercase text-indigo-400">2-Min Read</span>
            </div>
            <h2 className="text-white text-base font-heading font-bold mb-1.5">{dailyRead.title}</h2>
            <p className="text-slate-400 text-sm leading-relaxed">{dailyRead.body}</p>
          </div>

          {/* Tracker Shortcuts */}
          {trackerList.length > 0 && (
            <div data-testid="tracker-access-section">
              <h2 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-3 px-1">Your Trackers</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trackerList.map(t => {
                  const ps = PERM_STYLES[t.access] || PERM_STYLES.view;
                  const PermIcon = ps.icon;
                  return (
                    <button key={t.key} onClick={() => navigate(TRACKER_ROUTES[t.key] || '/employee')}
                      className="glass-card p-4 text-left hover:border-white/15 transition-all group cursor-pointer"
                      data-testid={`tracker-${t.key}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center shrink-0">
                            {TRACKER_ICONS[t.key] || <Briefcase size={20} className="text-slate-400" />}
                          </div>
                          <div>
                            <div className="text-white text-sm font-semibold group-hover:text-indigo-300 transition-colors">{t.label}</div>
                            <span className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${ps.bg} ${ps.text} border ${ps.border}`}>
                              <PermIcon size={9} />
                              {ps.label}
                            </span>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-slate-700 group-hover:text-indigo-400 transition-colors mt-1 shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TASKS */}
      {tab === 'tasks' && (
        <div className="space-y-4 animate-fadeIn" data-testid="emp-tasks">
          {tasks.length === 0 ? (
            <div className="glass-card p-8 text-center"><ListTodo size={32} className="text-slate-700 mx-auto mb-2" /><p className="text-slate-500 text-sm">No tasks assigned</p></div>
          ) : (
            <div className="space-y-2">
              {tasks.map(t => (
                <div key={t.task_id} className="glass-card p-4" data-testid={`emp-task-${t.task_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {TYPE_ICONS[t.task_type] || <Film size={14} className="text-slate-400" />}
                      <div>
                        <div className="text-white text-sm font-medium">{t.client_name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{t.task_type} {t.task_date ? `\u00B7 ${t.task_date}` : ''}</div>
                        {t.total_time && <div className="text-slate-600 text-xs font-mono mt-1">{t.total_time}</div>}
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[t.status]}`}>{t.status}</span>
                  </div>
                  {perms.production === 'edit' && t.status !== 'Completed' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                      {(t.status === 'Pending' || t.status === 'Paused') && (
                        <button onClick={() => handleTaskAction(t.task_id, 'start')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium transition-colors"><Play size={12} /> Start</button>
                      )}
                      {t.status === 'In Progress' && (
                        <>
                          <button onClick={() => handleTaskAction(t.task_id, 'pause')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium transition-colors"><Pause size={12} /> Pause</button>
                          <button onClick={() => setShowComplete(t.task_id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium transition-colors"><CheckCircle2 size={12} /> Complete</button>
                        </>
                      )}
                      {t.status === 'Paused' && (
                        <button onClick={() => setShowComplete(t.task_id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium transition-colors"><CheckCircle2 size={12} /> Complete</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* REIMBURSEMENTS */}
      {tab === 'reimbursements' && (
        <div className="space-y-4 animate-fadeIn" data-testid="emp-reimbursements">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-slate-500">Expenses</h2>
            <Button onClick={() => setShowReimburse(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="new-reimbursement"><Plus size={14} className="mr-1" /> New</Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-white">{reimbursements.length}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Filed</div></div>
            <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-emerald-400">{reimbursements.filter(r => r.status === 'Approved').length}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Approved</div></div>
            <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-amber-400">{reimbursements.filter(r => r.status === 'Pending').length}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Pending</div></div>
          </div>
          {reimbursements.length === 0 ? (
            <div className="glass-card p-8 text-center"><Wallet size={32} className="text-slate-700 mx-auto mb-2" /><p className="text-slate-500 text-sm">No reimbursements filed</p></div>
          ) : (
            <div className="space-y-2">
              {reimbursements.map(r => (
                <div key={r.id} className="glass-card p-4" data-testid={`reimb-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">{r.category}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{r.description}</div>
                      <div className="text-slate-600 text-[10px] mt-1">{new Date(r.created_at).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-heading font-bold">{'\u20B9'}{r.amount?.toLocaleString('en-IN')}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LEAVE */}
      {tab === 'leave' && (
        <div className="space-y-4 animate-fadeIn" data-testid="emp-leave">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-slate-500">Leave</h2>
            <Button onClick={() => setShowLeave(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="new-leave"><Plus size={14} className="mr-1" /> Apply</Button>
          </div>
          {profile?.leave_bank && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="leave-balance">
              <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-white">{profile.leave_bank.casual_leave ?? 0}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Casual</div></div>
              <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-white">{profile.leave_bank.sick_leave ?? 0}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Sick</div></div>
              <div className="glass-card p-3 text-center"><div className="text-xl font-heading font-black text-white">{profile.leave_bank.earned_leave ?? 0}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Earned</div></div>
              <div className="glass-card p-3 text-center border-emerald-500/20 border"><div className="text-xl font-heading font-black text-emerald-400">{(profile.leave_bank.casual_leave || 0) + (profile.leave_bank.sick_leave || 0) + (profile.leave_bank.earned_leave || 0)}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Total</div></div>
            </div>
          )}
          {leaveApps.length === 0 ? (
            <div className="glass-card p-8 text-center"><Calendar size={32} className="text-slate-700 mx-auto mb-2" /><p className="text-slate-500 text-sm">No leave applications</p></div>
          ) : (
            <div className="space-y-2">
              {leaveApps.map(l => (
                <div key={l.id} className="glass-card p-4" data-testid={`leave-${l.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">{l.leave_type}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{l.start_date} \u2014 {l.end_date} ({l.days} day{l.days > 1 ? 's' : ''})</div>
                      <div className="text-slate-600 text-xs mt-1">{l.reason}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[l.status]} shrink-0`}>{l.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PROFILE */}
      {tab === 'profile' && (
        <div className="space-y-4 animate-fadeIn" data-testid="emp-profile">
          {profile?.employee ? (
            <>
              <div className="glass-card p-5">
                <h2 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4">Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ProfileRow icon={<User size={14} />} label="Name" value={profile.employee.name} />
                  <ProfileRow icon={<Briefcase size={14} />} label="Employee ID" value={profile.employee.employee_id} />
                  <ProfileRow icon={<Mail size={14} />} label="Email" value={profile.employee.email} />
                  <ProfileRow icon={<Phone size={14} />} label="Phone" value={profile.employee.phone} />
                  <ProfileRow icon={<MapPin size={14} />} label="Department" value={profile.employee.department} />
                  <ProfileRow icon={<Briefcase size={14} />} label="Role" value={profile.employee.role} />
                  <ProfileRow icon={<Calendar size={14} />} label="Date of Birth" value={profile.employee.dob} />
                  <ProfileRow icon={<Calendar size={14} />} label="Joining Date" value={profile.employee.joining_date} />
                </div>
              </div>
              <div className="glass-card p-5">
                <h2 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4">Compensation</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <SalaryCard label="Base Salary" value={profile.employee.base_salary} />
                  <SalaryCard label="HRA" value={profile.employee.hra} />
                  <SalaryCard label="Allowances" value={profile.employee.allowances} />
                  <SalaryCard label="Bonus" value={profile.employee.bonus} />
                  <SalaryCard label="Deductions" value={profile.employee.deductions} negative />
                  <SalaryCard label="Total Salary" value={profile.employee.total_salary} highlight />
                </div>
              </div>
            </>
          ) : (
            <div className="glass-card p-8 text-center text-slate-500">Profile information not available</div>
          )}
        </div>
      )}

      {/* Dialogs */}
      {showComplete && (
        <Dialog open={true} onOpenChange={() => { setShowComplete(null); setFinalLink(''); }}>
          <DialogContent className="max-w-sm bg-[#0a0a0f] border-white/10">
            <DialogHeader><DialogTitle className="text-white font-heading">Complete Task</DialogTitle></DialogHeader>
            <div className="py-2">
              <label className="text-xs text-slate-500 block mb-1">Final Drive Link *</label>
              <Input value={finalLink} onChange={(e) => setFinalLink(e.target.value)} placeholder="https://drive.google.com/final..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="emp-final-link" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowComplete(null); setFinalLink(''); }} className="bg-white/5 border-white/10 text-white">Cancel</Button>
              <Button disabled={!finalLink} onClick={() => handleTaskAction(showComplete, 'complete', { final_drive_link: finalLink })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="emp-confirm-complete">Complete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {showReimburse && <ReimbursementDialog onClose={() => setShowReimburse(false)} onDone={() => { setShowReimburse(false); fetchData(); }} />}
      {showLeave && <LeaveDialog onClose={() => setShowLeave(false)} onDone={() => { setShowLeave(false); fetchData(); }} />}
    </SidebarLayout>
  );
}

function ProfileRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-slate-500">{icon}</div>
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-white text-sm">{value || '\u2014'}</div>
      </div>
    </div>
  );
}

function SalaryCard({ label, value, negative, highlight }) {
  return (
    <div className={`p-3 rounded-lg ${highlight ? 'bg-indigo-600/10 border border-indigo-500/20' : 'bg-white/[0.02] border border-white/5'}`}>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
      <div className={`font-heading text-lg font-bold mt-0.5 ${negative ? 'text-rose-400' : highlight ? 'text-indigo-400' : 'text-white'}`}>
        {value != null ? `\u20B9${Number(value).toLocaleString('en-IN')}` : '\u2014'}
      </div>
    </div>
  );
}

function ReimbursementDialog({ onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ amount: '', category: '', description: '', receipt_link: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/reimbursements`, { ...form, amount: parseFloat(form.amount) }, { withCredentials: true });
      toast.success('Reimbursement filed'); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader><DialogTitle className="text-white font-heading">File Reimbursement</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Amount ({'\u20B9'}) *</label>
              <Input type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="5000" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="reimb-amount" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Category *</label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="reimb-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {['Travel', 'Equipment', 'Software', 'Food', 'Medical', 'Other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <div><label className="text-xs text-slate-500 mb-1 block">Description *</label>
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What was the expense for?" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="reimb-description" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Receipt Link</label>
            <Input value={form.receipt_link} onChange={(e) => set('receipt_link', e.target.value)} placeholder="https://drive.google.com/receipt..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="reimb-receipt" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.amount || !form.category || !form.description} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-reimb">{loading ? 'Filing...' : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveDialog({ onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ leave_type: '', start_date: '', end_date: '', reason: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leave-applications`, form, { withCredentials: true });
      toast.success('Leave application submitted'); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader><DialogTitle className="text-white font-heading">Apply for Leave</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><label className="text-xs text-slate-500 mb-1 block">Leave Type *</label>
            <Select value={form.leave_type} onValueChange={(v) => set('leave_type', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="leave-type"><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                <SelectItem value="Casual Leave">Casual Leave</SelectItem>
                <SelectItem value="Sick Leave">Sick Leave</SelectItem>
                <SelectItem value="Earned Leave">Earned Leave</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">From *</label>
              <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="leave-start" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">To *</label>
              <Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="leave-end" /></div>
          </div>
          <div><label className="text-xs text-slate-500 mb-1 block">Reason *</label>
            <Input value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Reason for leave" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="leave-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.leave_type || !form.start_date || !form.end_date || !form.reason} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-leave">{loading ? 'Submitting...' : 'Apply'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
